import React, { useEffect, useMemo, useState } from "react";

import {
	Button,
	ConfirmModal,
	InfoMessage,
	Table,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ViewsService from "@/services/ViewsService";

import { useMonthPeriods } from "@/hooks/useMonthPeriods";
import { useViewActions } from "@/hooks/useViewActions";

import { ViewFormModal } from "@/components/organisms";
import { PageLayout } from "@/components/templates";

import { viewKeys } from "@/constants/CQueryKeys";

import { TViewSummary } from "@/types";

import {
	VIEW_LIST_COLUMNS,
	VIEW_LIST_ROW_ACTIONS,
} from "./ViewListView.columns";

const FILTERS = ["all", "archived"] as const;
type TFilter = (typeof FILTERS)[number];

const isFilter = (value: string | null): value is TFilter =>
	FILTERS.includes(value as TFilter);

/**
 * `/views` - every calendar the user reaches, with this month's
 * figures. A view is personal, so the list is one's own: the "archived"
 * tab narrows it to the views taken out of the sidebar. "New" opens the view
 * form in a modal; `?new=1` (the sidebar's "+", the old /calendar/create)
 * opens it on arrival.
 */
export const ViewListView: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { archive, remove } = useViewActions();
	const { current } = useMonthPeriods();

	const [isCreating, setIsCreating] = useState(false);
	const [editTarget, setEditTarget] = useState<TViewSummary | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<TViewSummary | null>(null);

	const filterParam = searchParams.get("filter");
	const filter: TFilter = isFilter(filterParam) ? filterParam : "all";

	useEffect(() => {
		if (searchParams.get("new") !== "1") return;
		setIsCreating(true);
		// The param did its job; a reload must not reopen the modal.
		const next = new URLSearchParams(searchParams);
		next.delete("new");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	const { isLoading, error, data } = useQuery({
		queryKey: viewKeys.summary(current),
		queryFn: () => ViewsService.getViewsSummary(current),
	});

	const views = useMemo(() => {
		const sorted = [...(data ?? [])].sort((a, b) =>
			a.name.localeCompare(b.name)
		);

		return "archived" === filter
			? sorted.filter((v) => v.isArchived)
			: sorted;
	}, [data, filter]);

	const openView = (view: TViewSummary) => navigate(`/views/${view.id}`);

	// Built inside the component (`t()` resolves after i18next.init()),
	// declared in ViewListView.columns.tsx.
	const columns = useMemo(() => VIEW_LIST_COLUMNS(), []);
	const rowActions = useMemo(
		() =>
			VIEW_LIST_ROW_ACTIONS({
				onEdit: setEditTarget,
				onArchive: (view) => void archive(view),
				onDelete: setDeleteTarget,
			}),
		[archive]
	);

	// Views are free on every plan: what the grid counts is projects,
	// clients and members.
	const createButton = {
		label: t("views.new"),
		color: "primary" as const,
		onClick: () => setIsCreating(true),
	};

	const table = (items: TViewSummary[]) => (
		<Table
			items={items}
			getItemKey={(view) => view.id}
			onRowClick={openView}
			columns={columns}
			rowActions={rowActions}
		/>
	);

	const isEmpty = !isLoading && (data ?? []).length === 0;

	return (
		<>
			<PageLayout
				title={t("views.title")}
				isLoading={isLoading}
				error={!!error}
				buttons={[createButton]}
				actionsPosition="aboveTabs"
				tabs={FILTERS.map((value) => ({
					value,
					label: t(`views.filters.${value}`),
				}))}
				selectedTabId={filter}
				onSelectTab={(value) => {
					const next = new URLSearchParams(searchParams);
					if (value === "all") next.delete("filter");
					else next.set("filter", value);
					setSearchParams(next, { replace: true });
				}}
				countMessage={
					data ? t("views.count", { count: data.length }) : undefined
				}
			>
				{isEmpty ? (
					<div
						className="flex flex-col items-start gap-4"
						data-testid="views-empty"
					>
						<InfoMessage>
							<span className="flex flex-col gap-1">
								<span className="font-medium">
									{t("views.emptyList")}
								</span>
								<span>{t("views.emptyHint")}</span>
							</span>
						</InfoMessage>
						<Button {...createButton}>{createButton.label}</Button>
					</div>
				) : (
					<div className="flex flex-col gap-6">
						{views.length > 0 ? (
							<section data-testid="views-list">
								{table(views)}
							</section>
						) : (
							<InfoMessage>{t("views.noResults")}</InfoMessage>
						)}
					</div>
				)}
			</PageLayout>
			<ViewFormModal
				open={isCreating}
				onClose={() => setIsCreating(false)}
				onSaved={(view) => navigate(`/views/${view.id}`)}
			/>
			<ViewFormModal
				open={!!editTarget}
				view={editTarget ?? undefined}
				onClose={() => setEditTarget(null)}
			/>
			<ConfirmModal
				open={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				size="sm"
				title={t("views.confirmDelete.title")}
				onConfirm={() => {
					void remove(deleteTarget!);
					setDeleteTarget(null);
				}}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			>
				<Trans
					i18nKey="views.confirmDelete.message"
					values={{ name: deleteTarget?.name }}
					components={{ b: <b /> }}
				/>
			</ConfirmModal>
		</>
	);
};
